/*
 * Copyright 2026 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.reminders

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Checkbox
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import studio.oddpods.cauce.core.formatter.CurrencyFormatter
import studio.oddpods.cauce.core.model.payment.PaymentMethod
import studio.oddpods.cauce.feature.reminders.generated.resources.Res
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_mark_paid_cancel
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_mark_paid_confirm
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_mark_paid_title
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_method_cash
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_method_mercadopago
import studio.oddpods.cauce.feature.reminders.generated.resources.feature_reminders_method_transfer

@Composable
fun MarkAsPaidDialog(
    dialog: MarkPaidDialogData,
    isMarking: Boolean,
    onToggleSession: (String) -> Unit,
    onMethodChange: (PaymentMethod) -> Unit,
    onConfirm: () -> Unit,
    onDismiss: () -> Unit,
    currencyFormatter: CurrencyFormatter = koinInject(),
) {
    val anySelected = dialog.sessions.any { it.selected }

    AlertDialog(
        onDismissRequest = { if (!isMarking) onDismiss() },
        title = { Text(stringResource(Res.string.feature_reminders_mark_paid_title)) },
        text = {
            Column(
                modifier = Modifier.verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(4.dp),
            ) {
                if (dialog.sessions.size > 1) {
                    dialog.sessions.forEach { session ->
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Checkbox(
                                checked = session.selected,
                                onCheckedChange = { onToggleSession(session.sessionId) },
                                enabled = !isMarking,
                            )
                            val dateLabel = session.formattedDate ?: session.sessionDateIso
                            Text("$dateLabel · ${currencyFormatter.format(session.amountOwed)}")
                        }
                    }
                }

                PaymentMethod.entries.forEach { method ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        RadioButton(
                            selected = dialog.method == method,
                            onClick = { onMethodChange(method) },
                            enabled = !isMarking,
                        )
                        Text(
                            text = when (method) {
                                PaymentMethod.MERCADOPAGO ->
                                    stringResource(Res.string.feature_reminders_method_mercadopago)
                                PaymentMethod.TRANSFER -> stringResource(Res.string.feature_reminders_method_transfer)
                                PaymentMethod.CASH -> stringResource(Res.string.feature_reminders_method_cash)
                            },
                        )
                    }
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick = onConfirm,
                enabled = !isMarking && anySelected,
            ) {
                Text(stringResource(Res.string.feature_reminders_mark_paid_confirm))
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss, enabled = !isMarking) {
                Text(stringResource(Res.string.feature_reminders_mark_paid_cancel))
            }
        },
    )
}
